/**
 * Plan G8 — post-signup marketing-setup nudge banner.
 *
 * Mounted at the layout level so it surfaces on any authenticated page
 * after the user's first build completes — until they either dismiss it
 * or connect at least one channel.
 *
 * Visibility is owned by ``useMarketingNudge`` (server-side state via
 * /api/onboarding/marketing-nudge/state). Dismiss is non-blocking and
 * optimistic — the banner disappears the moment the user clicks X.
 */
import { Link } from 'react-router-dom'
import { Sparkles, X, ArrowRight } from 'lucide-react'
import { useMarketingNudge } from '@/hooks/useMarketingNudge'

export function MarketingNudge() {
  const { show, dismiss, isDismissing } = useMarketingNudge()

  if (!show) return null

  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="marketing-nudge-banner"
      className="border-b border-lime/20 bg-gradient-to-r from-lime/10 via-lime/5 to-transparent px-4 py-3 sm:px-6"
    >
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 min-w-0">
          <span
            aria-hidden="true"
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-lime/20 text-lime"
          >
            <Sparkles className="h-4 w-4" />
          </span>
          <div className="min-w-0">
            <p className="text-sm font-medium text-white">
              Your app is built. Connect channels to launch marketing.
            </p>
            <p className="text-xs text-white/60 hidden sm:block">
              Wire up Resend, Google Ads, or Meta so Qyngent can run the
              marketing leg of your funnel.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Link
            to="/connectors"
            data-testid="marketing-nudge-cta"
            className="inline-flex items-center gap-1.5 rounded-md bg-lime px-3 py-1.5 text-xs font-semibold text-black hover:bg-lime/90 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-lime transition-colors"
          >
            Connect channels
            <ArrowRight className="h-3.5 w-3.5" />
          </Link>
          <button
            type="button"
            onClick={dismiss}
            disabled={isDismissing}
            data-testid="marketing-nudge-dismiss"
            aria-label="Dismiss marketing setup nudge"
            className="rounded-md p-1.5 text-white/60 hover:text-white hover:bg-white/5 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white/40 transition-colors disabled:opacity-50"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  )
}
